import { useCallback, useEffect, useRef, useState } from 'react';

export interface ElementSize {
  width: number;
  height: number;
}

export function useElementSize<T extends HTMLElement>() {
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });
  const roRef = useRef<ResizeObserver | null>(null);

  const read = useCallback((el: T) => {
    const { width, height } = el.getBoundingClientRect();

    setSize((s) =>
      s.width !== width || s.height !== height ? { width, height } : s
    );
  }, []);

  const observeElement = useCallback(
    (node: T | null) => {
      roRef.current?.disconnect();
      roRef.current = null;

      if (!node) return;

      read(node);

      if (typeof ResizeObserver === 'function') {
        const ro = new ResizeObserver((entries) => {
          const cr = entries[0]?.contentRect;

          if (cr) {
            const w = cr.width,
              h = cr.height;
            setSize((s) =>
              s.width !== w || s.height !== h ? { width: w, height: h } : s
            );
          } else {
            read(node);
          }
        });

        roRef.current = ro;
        try {
          ro.observe(node, { box: 'border-box' });
        } catch {
          ro.observe(node);
        }
      } else {
        const onResize = () => read(node);
        window.addEventListener('resize', onResize);

        roRef.current = {
          disconnect: () => window.removeEventListener('resize', onResize),
        } as ResizeObserver;
      }
    },
    [read]
  );

  useEffect(() => () => roRef.current?.disconnect(), []);

  return [size, observeElement] as const;
}
